import { useTranslation } from 'react-i18next';

import type { EjemploAhorros } from '#components/mobile/ahorros/EjemploAyuda';

import type { PasoAyuda, PreguntaAyuda } from './pasos';

type Pestana = 'navegando' | 'cumplidas';

/**
 * Pone la pantalla de Ahorros para un paso: la pestaña y lo que se enseña
 * de ejemplo encima (o nada), y espera a que `listo` esté pintado.
 */
export type PonerAhorros = (
  pestana: Pestana,
  ejemplo: EjemploAhorros | null,
  listo?: string,
) => Promise<void>;

function uno(selector: string): Element[] {
  const el = document.querySelector(selector);
  return el ? [el] : [];
}

function todos(selector: string): Element[] {
  return [...document.querySelectorAll(selector)];
}

/** El primero de los selectores que esté en pantalla. */
function primero(...selectores: string[]): Element[] {
  for (const s of selectores) {
    const el = document.querySelector(s);
    if (el) {
      return [el];
    }
  }
  return [];
}

function el<T extends Element = Element>(selector: string): T | null {
  return document.querySelector<T>(selector);
}

const TRAVESIA = '[data-testid="ahorros-travesia"]';
const BODEGA =
  '[data-testid="ahorros-repisa"], [data-testid="ahorros-cumplidas-vacio"]';

/** Categoría de la primera travesía de la lista (para enseñar su ficha). */
function primeraTravesia(): string | null {
  return el(TRAVESIA)?.getAttribute('data-categoria') ?? null;
}

function padreDe(selector: string): Element[] {
  const hijo = el(selector);
  return hijo?.parentElement ? [hijo.parentElement] : [];
}

/**
 * La ayuda de Ahorros (maqueta v2 aprobada por Vadik el 28-oct-2026): Gavi
 * entra en la bodega y en la bitácora, abre «Nueva travesía» y la hoja de
 * zarpar con su carta, la llegada de una botella y la ficha de una travesía.
 * Lo que sale relleno es un ejemplo: no se crea ni se guarda nada, y al
 * cerrar todo queda como estaba (`alSalir`). Nunca pulsa «Asignar»,
 * «Ponerle destino» ni «No». Las preguntas dependen de la pestaña.
 */
export function usePreguntasAhorros({
  pestana,
  poner,
}: {
  pestana: Pestana;
  poner: PonerAhorros;
}): PreguntaAyuda[] {
  const { t } = useTranslation();

  const saludo = t(
    'I save too: I have half a sardine buried on Llafranc beach. Destination: Tuesday.',
  );
  const final = t(
    "Done! Tap the porthole whenever you want to go over it again. My only voyage completed: a tourist's whole sandwich. No letter, mind you.",
  );

  /** Paso en la pestaña `p`, sin nada de ejemplo encima. */
  const en =
    (p: Pestana, listo?: string): PasoAyuda['preparar'] =>
    () =>
      poner(p, null, listo);

  const navegando = en('navegando', '[data-testid="ahorros-nueva"]');
  const cumplidas = en('cumplidas', BODEGA);
  const hayTravesias = () => !!el(TRAVESIA);
  const filaOVacio = () =>
    primero(
      TRAVESIA,
      '[data-testid="ahorros-nueva"]',
      '[data-testid="ahorros-vacio"]',
    );
  const ojo: PasoAyuda = {
    id: 'final',
    escena: 'propia',
    radio: 30,
    final: true,
    preparar: () => poner(pestana, null),
    texto: final,
    objetivo: () => uno('[data-testid="ojo-de-buey"]'),
  };

  // Bodega y bitácora (las enseñan las dos pestañas).
  const bitacora = (id: string, texto: string): PasoAyuda => ({
    id,
    escena: 'propia',
    radio: 14,
    pulsa: () => el('[data-testid="ahorros-bitacora"]'),
    preparar: () =>
      poner('cumplidas', { que: 'bitacora' }, '[data-testid="cuaderno"]'),
    texto,
    objetivo: () =>
      primero('[data-testid="cuaderno"]', '[data-testid="escritorio"]'),
  });
  const total = (id: string, texto: string): PasoAyuda => ({
    id,
    escena: 'propia',
    radio: 12,
    preparar: cumplidas,
    texto,
    objetivo: () => uno('[data-ayuda="ahorros-total"]'),
  });

  // La llegada de una botella (de ejemplo).
  const llegada = (id: string, texto: string): PasoAyuda => ({
    id,
    escena: 'propia',
    radio: 16,
    preparar: () =>
      poner(pestana, { que: 'llegada' }, '[data-testid="llegada-botella"]'),
    texto,
    objetivo: () => uno('[data-testid="llegada-carta"]'),
  });
  const guardarLlegada = (id: string, texto: string): PasoAyuda => ({
    id,
    escena: 'propia',
    radio: 14,
    preparar: () =>
      poner(pestana, { que: 'llegada' }, '[data-testid="llegada-botella"]'),
    texto,
    objetivo: () => uno('[data-testid="llegada-guardar"]'),
  });

  // La ficha de la primera travesía.
  const ficha = (
    id: string,
    texto: string,
    objetivo: () => Element[],
    pulsa?: boolean,
  ): PasoAyuda => ({
    id,
    escena: 'propia',
    radio: 14,
    disponible: hayTravesias,
    pulsa: pulsa ? () => el(TRAVESIA) : undefined,
    preparar: async () => {
      const categoriaId = primeraTravesia();
      if (categoriaId) {
        await poner(
          'navegando',
          { que: 'ficha', categoriaId },
          '[data-testid="botella-ficha"]',
        );
      }
    },
    texto,
    objetivo,
  });
  const fichaRitmo = () =>
    todos(
      '[data-testid="botella-llevas"], [data-testid="botella-ritmo"], [data-testid="botella-cuota"]',
    );

  if (pestana === 'cumplidas') {
    const recorrido: PasoAyuda[] = [
      {
        id: 'c-hola',
        escena: 'propia',
        radio: 14,
        preparar: cumplidas,
        texto: `${t(
          'Hi! This is your hold: the voyages that have already arrived. I will tell you step by step.',
        )} ${saludo}`,
        objetivo: () => uno('[data-ayuda="titulo-ahorros"]'),
      },
      {
        id: 'c-repisa',
        escena: 'propia',
        radio: 16,
        preparar: cumplidas,
        texto: t(
          'Every voyage that arrives leaves its bottle on this shelf, opened.',
        ),
        objetivo: () => uno(BODEGA),
      },
      total(
        'c-total',
        t('Everything you have saved on the voyages that arrived.'),
      ),
      {
        id: 'c-cada',
        escena: 'propia',
        radio: 14,
        preparar: cumplidas,
        texto: t(
          'Each one, with the day it arrived and what you saved. Tap it and its page opens.',
        ),
        objetivo: () => uno('[data-testid="ahorros-cumplida"]'),
      },
      bitacora(
        'c-bitacora',
        t(
          'Your logbook: the first sheet adds up the totals; with the arrows you go to the page of each arrival.',
        ),
      ),
      {
        id: 'c-repisa-bitacora',
        escena: 'propia',
        radio: 12,
        preparar: () =>
          poner('cumplidas', { que: 'bitacora' }, '[data-testid="cuaderno"]'),
        texto: t('Up top, the shelf: tap a bottle to go to its page.'),
        objetivo: () => uno('[data-testid="repisa"]'),
      },
      ojo,
    ];
    const meta: PasoAyuda[] = [
      {
        id: 'f-aviso',
        escena: 'propia',
        radio: 16,
        preparar: cumplidas,
        texto: t(
          'When a voyage reaches its goal, I let you know on Home: "A bottle has arrived".',
        ),
        objetivo: () => uno(BODEGA),
      },
      llegada(
        'f-abre',
        t(
          'You tap it: the bottle rises from the sea, the wax seal pops and you read your letter. This one is an example.',
        ),
      ),
      guardarLlegada(
        'f-guardar',
        t('"Save to the logbook" and it stays in your hold, with its page.'),
      ),
      {
        id: 'f-repisa',
        escena: 'propia',
        radio: 16,
        final: true,
        preparar: cumplidas,
        texto: t(
          'And here it is, on the shelf. It is never deleted. The one I showed you was an example: nothing was saved.',
        ),
        objetivo: () => uno(BODEGA),
      },
    ];
    return [
      { id: 'todo', texto: t('Tell me about this screen'), pasos: recorrido },
      {
        id: 'meta',
        texto: t('What happens when I reach the goal?'),
        pasos: meta,
      },
    ];
  }

  const recorrido: PasoAyuda[] = [
    {
      id: 'hola',
      escena: 'propia',
      radio: 14,
      preparar: navegando,
      texto: `${t(
        'Hi! Here is the money you set aside for something you choose: a trip, a car, a cushion for surprises. I will tell you step by step.',
      )} ${saludo}`,
      objetivo: () => uno('[data-ayuda="titulo-ahorros"]'),
    },
    {
      id: 'pestanas',
      escena: 'propia',
      radio: 14,
      preparar: navegando,
      texto: t(
        '"At sea": your voyages under way. "Arrived": your hold, with the ones that have already arrived. We will go in later.',
      ),
      objetivo: () => uno('[data-testid="ahorros-pestana-segmentado"]'),
    },
    {
      id: 'sin-destino',
      escena: 'propia',
      radio: 16,
      disponible: () => !!el('[data-testid="ahorros-sin-destino"]'),
      preparar: navegando,
      texto: t(
        'If a category looks like savings but has no goal, I let you know here. "Give it a destination" means giving it an amount and a date; with "No", I will not ask about it again.',
      ),
      objetivo: () => uno('[data-testid="ahorros-sin-destino"]'),
    },
    {
      id: 'resumen',
      escena: 'propia',
      radio: 16,
      disponible: () => !!el('[data-testid="ahorros-resumen"]'),
      preparar: navegando,
      texto: t(
        'What you have set aside this month, adding up all your voyages, out of what they all ask for.',
      ),
      objetivo: () => uno('[data-testid="ahorros-resumen"]'),
    },
    {
      id: 'travesia',
      escena: 'propia',
      radio: 14,
      disponible: hayTravesias,
      preparar: navegando,
      texto: t(
        'Each row is a voyage: what you have, the goal and the date you want to arrive. Tap it and its card opens.',
      ),
      objetivo: () => uno(TRAVESIA),
    },
    {
      id: 'cuota',
      escena: 'propia',
      radio: 10,
      disponible: () => !!el('[data-ayuda="ahorros-cuota"]'),
      preparar: navegando,
      texto: t(
        'What to set aside this month to arrive on time: what is missing, shared out among the months left.',
      ),
      objetivo: () => uno('[data-ayuda="ahorros-cuota"]'),
    },
    {
      id: 'asignar',
      escena: 'propia',
      radio: 14,
      disponible: () => !!el('[data-testid="ahorros-asignar"]'),
      preparar: navegando,
      texto: t(
        '"Assign" puts what is missing this month into that category, from your money to assign. A notice with "Undo" appears. You tap it; I only point at it.',
      ),
      objetivo: () => uno('[data-testid="ahorros-asignar"]'),
    },
    {
      id: 'apartado',
      escena: 'propia',
      radio: 10,
      disponible: () => !!el('[data-testid="ahorros-apartado"]'),
      preparar: navegando,
      texto: t(
        '"✓ Set aside": this month is done. The money stays in your account; in the Plan it is reserved for this voyage.',
      ),
      objetivo: () => uno('[data-testid="ahorros-apartado"]'),
    },
    {
      id: 'nueva',
      escena: 'propia',
      radio: 16,
      preparar: navegando,
      texto: t(
        '"New voyage" creates one. In "How do I start a voyage?" I open it for you and we fill it in step by step.',
      ),
      objetivo: () => uno('[data-testid="ahorros-nueva"]'),
    },
    {
      id: 'bodega',
      escena: 'propia',
      radio: 16,
      pulsa: () => el('[data-testid="ahorros-pestana-cumplidas"]'),
      preparar: cumplidas,
      texto: t(
        'This is your hold. Every voyage that arrives leaves its bottle here, opened.',
      ),
      objetivo: () => uno(BODEGA),
    },
    total(
      'total',
      t('And this is everything you saved on the voyages that arrived.'),
    ),
    bitacora(
      'bitacora',
      t(
        '"Open your logbook": a notebook with one page for each arrival, with its letter and its date. The first sheet adds up the totals.',
      ),
    ),
    ojo,
  ];

  const nombreEjemplo = '[data-testid="nueva-nombre"]';
  const empezar: PasoAyuda[] = [
    {
      id: 'b-empezar',
      escena: 'propia',
      radio: 16,
      preparar: navegando,
      texto: t(
        'You start with "New voyage". I will open it to show you. What I fill in is an example: nothing is saved.',
      ),
      objetivo: () => uno('[data-testid="ahorros-nueva"]'),
    },
    {
      id: 'b-nombre',
      escena: 'propia',
      radio: 14,
      pulsa: () => el('[data-testid="ahorros-nueva"]'),
      preparar: () =>
        poner(
          'navegando',
          { que: 'nueva', eligiendo: false, conGrupo: false },
          nombreEjemplo,
        ),
      texto: t(
        'First, the name: what you are saving for. With the + you give it an emoji.',
      ),
      objetivo: () => padreDe(nombreEjemplo),
    },
    {
      id: 'b-grupo',
      escena: 'propia',
      radio: 14,
      pulsa: () => el('[data-testid="nueva-grupo"]'),
      preparar: () =>
        poner(
          'navegando',
          { que: 'nueva', eligiendo: true, conGrupo: false },
          '[data-testid="hoja-seleccion-lista"]',
        ),
      texto: t(
        'Then, which group of the Plan it goes in. Choose one or create one with "New group".',
      ),
      objetivo: () => uno('[data-testid="hoja-seleccion-lista"]'),
    },
    {
      id: 'b-continuar',
      escena: 'propia',
      radio: 16,
      pulsa: () =>
        el(
          '[data-testid="hoja-seleccion-lista"] [role="button"], [data-testid="hoja-seleccion-lista"] button',
        ),
      preparar: () =>
        poner(
          'navegando',
          { que: 'nueva', eligiendo: false, conGrupo: true },
          '[data-testid="nueva-crear"]',
        ),
      texto: t(
        'With a name and a group, "Continue". The voyage is also a category of your Plan: that is where it is born.',
      ),
      objetivo: () => uno('[data-testid="nueva-crear"]'),
    },
    {
      id: 'b-tipo',
      escena: 'propia',
      radio: 14,
      pulsa: () => el('[data-testid="nueva-crear"]'),
      preparar: () =>
        poner(
          'navegando',
          { que: 'zarpar', lleno: false },
          '[data-testid="hoja-zarpar"]',
        ),
      texto: t(
        'Now, setting sail. Is it a one-off, like this trip, or something you repeat every year, like the summer holidays?',
      ),
      objetivo: () => uno('[data-testid="zarpar-tipo-segmentado"]'),
    },
    {
      id: 'b-cuanto',
      escena: 'propia',
      radio: 16,
      pulsa: () => el('[data-testid="zarpar-importe"]'),
      preparar: async () => {
        await poner(
          'navegando',
          { que: 'zarpar', lleno: true },
          '[data-testid="zarpar-importe"]',
        );
        if (!el('[data-testid="zarpar-importe-hoja"]')) {
          el<HTMLElement>('[data-testid="zarpar-importe"]')?.click();
          await poner(
            'navegando',
            { que: 'zarpar', lleno: true },
            '[data-testid="zarpar-importe-hoja"]',
          );
        }
      },
      texto: t('How much you need, with the Rumbo keypad.'),
      objetivo: () => uno('[data-testid="zarpar-importe-hoja"]'),
    },
    {
      id: 'b-cuando',
      escena: 'propia',
      radio: 12,
      preparar: async () => {
        el<HTMLElement>('[data-testid="zarpar-importe-hecho"]')?.click();
        await poner(
          'navegando',
          { que: 'zarpar', lleno: true },
          '[data-testid="zarpar-fecha"]',
        );
      },
      texto: t(
        'And by when. With the amount and the date I work out how much to set aside each month.',
      ),
      objetivo: () => uno('[data-testid="zarpar-fecha"]'),
    },
    zarparLleno(
      'b-para-que',
      t(
        'What is it for? Choose a drawing: it changes the bottle and the seal you will see when you arrive.',
      ),
      () => padreDe('[data-testid="zarpar-clase-viaje"]'),
    ),
    zarparLleno(
      'b-carta',
      t(
        'The letter: a note for the day you arrive. Why you want this. It is optional and only you read it.',
      ),
      () => padreDe('[data-testid="travesia-carta"]'),
    ),
    zarparLleno(
      'b-plan',
      t(
        '"When…, I set aside…": your plan to make it, for example "When I get paid, I set aside €75". I remind you; nothing is set aside on its own.',
      ),
      () => padreDe('[data-testid="travesia-si-entonces"]'),
    ),
    zarparLleno(
      'b-abrir',
      t(
        'And when to open the letter: on arrival, or keep it sealed until a date, like a birthday.',
      ),
      () => uno('[data-testid="travesia-abrir-segmentado"]'),
    ),
    {
      id: 'b-zarpa',
      escena: 'propia',
      radio: 14,
      final: true,
      pulsa: () => el('[data-testid="zarpar-guardar"]'),
      preparar: navegando,
      texto: t(
        '"Set sail" and it is under way, in this list, with what to set aside this month. It was my example, so nothing was saved: everything stays as it was.',
      ),
      objetivo: () =>
        primero(
          '[data-testid="ahorros-lista"]',
          '[data-testid="ahorros-vacio"]',
          '[data-testid="ahorros-nueva"]',
        ),
    },
  ];

  const carta: PasoAyuda[] = [
    zarparLleno(
      'k-nota',
      t(
        'The letter is a note to your future self: why you want this. It goes inside the bottle, sealed.',
      ),
      () => padreDe('[data-testid="travesia-carta"]'),
    ),
    zarparLleno(
      'k-cerrada',
      t(
        'You do not read it until you reach the goal, or until the date you choose.',
      ),
      () => uno('[data-testid="travesia-abrir-segmentado"]'),
    ),
    llegada(
      'k-llegas',
      t(
        'The day you arrive, the bottle rises from the sea, the wax seal pops and your letter appears.',
      ),
    ),
    guardarLlegada(
      'k-bitacora',
      t('Then you keep it in your logbook, with its date and what you saved.'),
    ),
    {
      id: 'k-mientras',
      escena: 'propia',
      radio: 14,
      final: true,
      preparar: navegando,
      texto: t(
        'While you save, it reminds you why you are doing it. When you feel like spending it on something else, it helps. All of this was an example: nothing was saved.',
      ),
      objetivo: filaOVacio,
    },
  ];

  const diferencia: PasoAyuda[] = [
    {
      id: 'd-normal',
      escena: 'propia',
      radio: 14,
      preparar: navegando,
      texto: t(
        'A normal category is for the month\'s spending: groceries, going out. Bills that repeat, like electricity, go in "Fixed payments".',
      ),
      objetivo: filaOVacio,
    },
    {
      id: 'd-travesia',
      escena: 'propia',
      radio: 14,
      preparar: navegando,
      texto: t(
        'A voyage is money you gather month by month for something with an amount and a date. It is still a category of the Plan, but with a destination.',
      ),
      objetivo: filaOVacio,
    },
    ficha(
      'd-rumbo',
      t(
        'That is why I tell you how much you have, when you arrive at this pace and how much is due this month.',
      ),
      fichaRitmo,
      true,
    ),
    ficha(
      'd-botella',
      t(
        'It has its bottle at sea and its letter. When it arrives it opens and stays in your hold. Normal ones do not.',
      ),
      () => uno('[data-testid="botella-ficha"] > div'),
    ),
    ficha('d-crece', t('And here you see how it grows towards the goal.'), () =>
      uno('[data-testid="ficha-evolucion"]'),
    ),
  ];
  diferencia[diferencia.length - 1].final = true;

  const cuanto: PasoAyuda[] = [
    {
      id: 'a-cuota',
      escena: 'propia',
      radio: 10,
      disponible: () => !!el('[data-ayuda="ahorros-cuota"]'),
      preparar: navegando,
      texto: t(
        'Next to each voyage you see what is due this month: what is missing for the goal, shared out among the months left.',
      ),
      objetivo: () => uno('[data-ayuda="ahorros-cuota"]'),
    },
    {
      id: 'a-todas',
      escena: 'propia',
      radio: 16,
      disponible: () => !!el('[data-testid="ahorros-resumen"]'),
      preparar: navegando,
      texto: t(
        'Up top, the sum: how much you have set aside this month out of what all your voyages ask for.',
      ),
      objetivo: () => uno('[data-testid="ahorros-resumen"]'),
    },
    {
      id: 'a-asignar',
      escena: 'propia',
      radio: 14,
      final: true,
      disponible: () => !!el('[data-testid="ahorros-asignar"]'),
      preparar: navegando,
      texto: t(
        'With "Assign" you put what is missing in one tap, from the money to assign. If you get it wrong, "Undo" in the notice.',
      ),
      objetivo: () => uno('[data-testid="ahorros-asignar"]'),
    },
  ];

  const sinDestino: PasoAyuda[] = [
    {
      id: 's-que',
      escena: 'propia',
      radio: 16,
      disponible: () => !!el('[data-testid="ahorros-sin-destino"]'),
      preparar: navegando,
      texto: t(
        'This category sounds like savings and keeps money, but it has no goal. Without a goal I do not know how much to set aside or when you arrive.',
      ),
      objetivo: () => uno('[data-testid="ahorros-sin-destino"]'),
    },
    {
      id: 's-decides',
      escena: 'propia',
      radio: 14,
      final: true,
      disponible: () => !!el('[data-testid="ahorros-sin-destino-fila"]'),
      preparar: navegando,
      texto: t(
        '"Give it a destination": you give it an amount and a date and it becomes a voyage. "No": I leave it as it is and will not ask you again.',
      ),
      objetivo: () => uno('[data-testid="ahorros-sin-destino-fila"]'),
    },
  ];

  const cambiar: PasoAyuda[] = [
    ficha(
      'm-ficha',
      t(
        'Tap the voyage and its card opens. That is where you change the goal and the date; the letter, from its compass.',
      ),
      () =>
        primero(
          '[data-testid="objetivo-resumen"]',
          '[data-testid="botella-ficha"]',
        ),
      true,
    ),
    ficha(
      'm-recalcula',
      t(
        "If you change the goal or the date, each month's amount is worked out again on its own.",
      ),
      () =>
        primero(
          '[data-testid="botella-cuota"]',
          '[data-testid="botella-ficha"]',
        ),
    ),
  ];
  cambiar[cambiar.length - 1].final = true;

  const cuenta: PasoAyuda[] = [
    {
      id: 'e-no',
      escena: 'propia',
      radio: 14,
      preparar: navegando,
      texto: t(
        'No. Setting aside does not move money between accounts: it stays where it is, in your bank.',
      ),
      objetivo: filaOVacio,
    },
    {
      id: 'e-reservado',
      escena: 'propia',
      radio: 14,
      final: true,
      preparar: navegando,
      texto: t(
        'What changes is the Plan: that money is reserved for the voyage and does not count for other spending.',
      ),
      objetivo: () =>
        primero(
          '[data-testid="ahorros-apartado"]',
          '[data-ayuda="ahorros-cuota"]',
          TRAVESIA,
          '[data-testid="ahorros-nueva"]',
        ),
    },
  ];

  return [
    { id: 'todo', texto: t('Tell me about this screen'), pasos: recorrido },
    {
      id: 'empezar',
      texto: t('How do I start a voyage?'),
      pasos: empezar,
    },
    { id: 'carta', texto: t('What is the letter for?'), pasos: carta },
    {
      id: 'diferencia',
      texto: t('How is it different from a normal category?'),
      pasos: diferencia,
    },
    {
      id: 'cuanto',
      texto: t('How much do I have to set aside this month?'),
      pasos: cuanto,
    },
    {
      id: 'sin-destino',
      texto: t('What does "has no destination" mean?'),
      pasos: sinDestino,
    },
    {
      id: 'cambiar',
      texto: t('How do I change the goal or the date?'),
      pasos: cambiar,
    },
    {
      id: 'cuenta',
      texto: t('Does the money move between accounts?'),
      pasos: cuenta,
    },
  ];

  /** Un paso sobre la hoja de zarpar de ejemplo ya escrita. */
  function zarparLleno(
    id: string,
    texto: string,
    objetivo: () => Element[],
  ): PasoAyuda {
    return {
      id,
      escena: 'propia',
      radio: 14,
      preparar: async () => {
        el<HTMLElement>('[data-testid="zarpar-importe-hecho"]')?.click();
        await poner(
          'navegando',
          { que: 'zarpar', lleno: true },
          '[data-testid="seccion-carta"]',
        );
      },
      texto,
      objetivo,
    };
  }
}
